import { Control, Controller, FieldErrors, useFieldArray, UseFormRegister, UseFormWatch } from "react-hook-form";
import { CompanyBranchValidationSchema } from "../../schemas/createBranch.schema";
import { formatCurrency } from "@/common/utils/formatters";
import { CircleAlert } from "lucide-react";
import Select from "@/common/components/Select";
import { CreditProductCategoryConfig } from "./creditProductCategoryConfig";
import Accordion from "@/common/components/Accordion";
import { CompanyData } from "../../types/companies.type";
import Checkbox from "@/common/components/Checkbox";
import { CurrencyTextField } from "@/common/components/CurrencyTextField";
import { Option } from "@/common/types/option";
import { CompanyBranchData } from "../../types/companyBranches.type";
import { useParams } from 'react-router-dom';
import { useClientCompaniesAvailableToAllocateInBranchesSummary } from '../../hooks/useClientCompaniesAvailableToAllocateInBranchesSummary';
import { ClientCompanyAllowedCategoryAndProductData, ClientCompanyAllowedCategoriesAndProducts } from '../../types/allowedCategoriesAndProducts.type';
import { ClientCompanyAllowedCategoriesAndProductsTransformer } from '../../services/allowedCategoriesAndProductsTransformer';
import { filterOptions } from "@/features/users/constants/filterOptions.constant";
import { FilterOptionOption } from "react-select";
import useDebounce from "@/common/hooks/useDebounce";
import { ControlledByType } from "../../types/companyGovernmentAllocation.type";
import { ContractProductCategoryData } from "@/features/publicCompanyLeadsFunnel/types/publicCompanyLead";

interface CreditConfigurationProps {
  register: UseFormRegister<CompanyBranchValidationSchema>;
  control: Control<CompanyBranchValidationSchema>;
  watch: UseFormWatch<CompanyBranchValidationSchema>
  errors: FieldErrors<CompanyBranchValidationSchema>;
  companyData?: CompanyData;
  branchData?: CompanyBranchData;
  consumptionControlledBy?: ControlledByType;
  contractProductCategories?: ContractProductCategoryData[];
}

export const CreditConfiguration = ({
  register,
  control,
  watch,
  errors,
  companyData,
  branchData,
  consumptionControlledBy = 'currency',
  contractProductCategories,
}: CreditConfigurationProps) => {
  const { companyId } = useParams();

  const isQuantity = consumptionControlledBy === 'quantity';

  const [searchProduct, setSearchProduct] = useDebounce('')

  const {
    fields: categoryGrantedCreditFields,
    append: appendCategoryGrantedCredit,
    remove: removeCategoryGrantedCredit
  } = useFieldArray({
    control,
    name: "product_category_granted_credits_attributes",
  });

  const enableCreditConfiguration = watch("enableCreditConfiguration");

  const {
    data: allowedCategoriesAndProducts,
  } = useClientCompaniesAvailableToAllocateInBranchesSummary<ClientCompanyAllowedCategoriesAndProducts>({
    companyId,
    filters: { search: searchProduct },
  });

  const allowedCategoriesOptions = ClientCompanyAllowedCategoriesAndProductsTransformer.optionsTransformer({
    key: 'categories',
    watch,
    controlledBy: consumptionControlledBy,
  })(allowedCategoriesAndProducts || {});

  const handleAppendCategory = (option: Option & ClientCompanyAllowedCategoryAndProductData) => {
    if (option.value) {
      if (isQuantity) {
        appendCategoryGrantedCredit({
          product_category_id: option.value,
          display_name: option.display_name,
          granted_credit: "",
          granted_quantity: "",
          product_granted_credits_attributes: [],
        } as unknown as Parameters<typeof appendCategoryGrantedCredit>[0]);
      } else {
        appendCategoryGrantedCredit({
          product_category_id: option.value,
          display_name: option.display_name,
          granted_credit: "",
          product_granted_credits_attributes: [],
        });
      }
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="font-bold text-lg">
        {companyData?.kind === 'public_company' ? 'Configurações de Cota' : 'Configurações de crédito'}
      </p>

      <div className="flex flex-col gap-2">
        <Controller
          name="granted_credit"
          control={control}
          render={({ field }) => (
            <CurrencyTextField
              label={
                companyData?.kind === 'public_company' ? 'Cota de empenho da filial' : 'Crédito garantido a filial'
              }
              markAsRequired
              data-testid="granted_credit_input"
              allowZero={true}
              errorMessage={errors.granted_credit?.message || undefined}
              {...field}
            />
          )}
        />

        <div className="flex items-center gap-4">
          {companyData?.granted_credit && (
            <p className="text-[12px] font-light italic">
              {companyData?.kind === 'public_company' ? 'Cota Global do Contrato' : 'Crédito garantido a empresa'}:{' '}
              {formatCurrency(Number(companyData?.granted_credit))}
            </p>
          )}

          {companyData?.unallocated_credit && (
            <p className="text-[12px] font-light italic">
              {companyData?.kind === 'public_company' ? 'Cota livre para alocação' : 'Crédito livre para alocação'}:{' '}
              {formatCurrency(Number(companyData?.unallocated_credit))}
            </p>
          )}

          {branchData && companyData?.kind === 'public_company' && (
            <p className="text-[12px] font-light italic">
              Crédito empenhado na filial:{' '}
              {formatCurrency(Number(branchData?.government_allocation_requested_credit || 0))}
            </p>
          )}

          {branchData?.consumed_credit && (
            <p className="text-[12px] font-light italic">
              Crédito consumido:{' '}
              {formatCurrency(Number(branchData?.consumed_credit))}
            </p>
          )}
        </div>
      </div>

      <Accordion
        name="creditConfiguration"
        head={(
          <div className="flex flex-col gap-2">
            {errors.enableCreditConfiguration && (
              <p className="text-sm text-danger-soft">
                {errors.enableCreditConfiguration.message || undefined}
              </p>
            )}

            <Checkbox
              label="Crédito garantido por serviço"
              onClick={(event) => event.stopPropagation()}
              {...register("enableCreditConfiguration")}
            />
          </div>
        )}
        body={(
          <div className="flex flex-col gap-4">
            <Select
              name="search_product_categories"
              placeholder="Pesquisar serviços"
              options={allowedCategoriesOptions?.filter((productCategory) => {
                return !categoryGrantedCreditFields?.some(({ product_category_id }) => product_category_id === productCategory.value)
              }) || []}
              onChange={(option) => handleAppendCategory(option as Option & ClientCompanyAllowedCategoryAndProductData)}
              onInputChange={setSearchProduct}
              filterOption={filterOptions as unknown as (option: FilterOptionOption<unknown>, inputValue: string) => boolean}
              value=""
              isDisabled={!enableCreditConfiguration}
            />

            <div className="flex items-center gap-2">
              <CircleAlert strokeWidth={1} color="var(--warning)" />

              <div>
                <p className="text-[12px]">
                  Ao selecionar um Serviço e/ou Produto, todos os não selecionados serão bloqueados para consumo
                </p>

                <p className="text-[12px] font-light italic">
                  Certifique-se de adicionar as restrições para todos os serviços e produtos que desejar consumir
                </p>
              </div>
            </div>

            {categoryGrantedCreditFields.map((category, index) => (
              <CreditProductCategoryConfig
                key={index}
                categoryId={category.product_category_id}
                displayName={category.display_name}
                categoryIndex={index}
                removeCategory={() => removeCategoryGrantedCredit(index)}
                control={control}
                register={register}
                errors={errors}
                disabled={!enableCreditConfiguration}
                watch={watch}
                companyKind={companyData?.kind}
                controlledBy={consumptionControlledBy}
                contractProductCategory={contractProductCategories?.find(
                  (contractProductCategory) => contractProductCategory.product_category?.id === category.product_category_id,
                )}
              />
            ))}
          </div>
        )}
      />
    </div>
  );
};
